<template>
  <div class="alert-center__comment-card">
    <h5 class="alert-center__comment-title">
      {{ $t('notification.text.noteAndComment') }}
    </h5>
    <div class="mc-notes-input">
      <FormulateInput
        v-model="localNote"
        type="text"
        name="notes"
        :data-me="`${dataMePrefix}-input-note`"
        :placeholder="$t('notification.text.giveNoteOrComment')"
        validation="max:255,length"
        error-behavior="live"
        @input="$emit('input', $event)"
      />
      <McButton
        :data-me="`${dataMePrefix}-btn-save`"
        text=""
        size="md"
        :icon="isSavePending ? null : 'icon-outline_paper-plane'"
        variant="outline-primary"
        :disabled="isSaveDisabled"
        :loading="isSavePending"
        @click="handleSave"
      />
    </div>
    <div v-if="userLogs.length > 0" class="alert-center-comment__logs">
      <div v-for="(group, index) in groupedUserLogs" :key="index" class="user-logs__wrapper">
        <div class="mb-2">
          <span>{{ group.date }}</span>
        </div>

        <div
          v-for="(log, logIndex) in group.logs"
          :key="`${log.createdOn}-${log.typeField}-${logIndex}`"
          class="user-logs__item"
        >
          <div class="user-logs-content">
            <i class="mc-fa icon-fill_checkmark-circle"></i>
            <div>
              <span class="user-logs-user">
                <strong>{{ log.user?.fullname }}</strong>
              </span>
              <template v-if="log.typeField === 'Resolve'">
                <span>
                  {{ $t('notification.text.markAs').toLowerCase() }}
                </span>
                <span :class="log.data === 'true' ? 'text-success' : 'text-danger'">
                  {{ log.data === 'true' ? resolvedText : unresolvedText }}
                </span>
              </template>
              <template v-else-if="log.typeField === 'FalseAlert'">
                <span v-if="log.data.includes('true')">
                  {{ $t('notification.text.markAs').toLowerCase() }}
                  <span class="text-danger">{{ falseAlertText }}</span>
                </span>
                <span v-else>
                  {{ $t('notification.text.canceled') }}
                  <span class="text-danger">{{ falseAlertText }}</span>
                </span>
              </template>
              <template v-else-if="log.typeField === 'Feedback'">
                <span>&nbsp;{{ `${log.data}` }}</span>
              </template>
              <template v-else>
                <span v-if="log.data === 'true'">
                  {{ $t('notification.text.markAs').toLowerCase() }}
                  <span class="text-danger">{{ falseAlertText }}</span>
                </span>
                <span v-else>
                  {{ $t('notification.text.markAs').toLowerCase() }}
                  <span class="text-danger">{{ unresolvedText }}</span>
                </span>
              </template>
            </div>
          </div>
          <div class="user-logs-time">
            <span>
              {{ momentTime(log.createdOn) }}
            </span>
            {{ getLabelTimezone() }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'

export default {
  name: 'AlertCenterCommentCard',

  components: { McButton },

  mixins: [Helper],

  props: {
    note: {
      type: String,
      default: '',
    },
    userLogs: {
      type: Array,
      default: () => [],
    },
    saveAction: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      localNote: this.note || '',
      pendingEvents: {},
      falseAlertText: 'False Alert',
      resolvedText: 'Resolved',
      unresolvedText: 'Unresolved',
    }
  },

  computed: {
    dataMePrefix() {
      return this.$attrs['data-me'] || 'alert-center-comment-card'
    },
    groupedUserLogs() {
      if (!this.userLogs || this.userLogs.length === 0) return []
      const groups = this.userLogs[0]?.logs ? this.userLogs : this.groupLogsByDate(this.userLogs)

      return groups.map((group) => ({
        ...group,
        logs: group.logs.flatMap((log) => this.expandFalseAlertLogs(log)),
      }))
    },
    isSavePending() {
      return Boolean(this.pendingEvents.save)
    },
    isSaveDisabled() {
      return this.isSavePending || !this.localNote?.trim()
    },
  },

  watch: {
    note(value) {
      if (value !== this.localNote) {
        this.localNote = value || ''
      }
    },
  },

  methods: {
    expandFalseAlertLogs(log) {
      if (log.typeField !== 'FalseAlert' || !log.data?.includes('true')) {
        return [log]
      }

      const feedback = log.data.split('|')[1]
      if (!feedback) return [log]

      return [
        log,
        {
          ...log,
          typeField: 'Feedback',
          data: feedback,
        },
      ]
    },
    async handleSave() {
      if (this.pendingEvents.save || !this.localNote?.trim()) return

      this.$set(this.pendingEvents, 'save', true)

      try {
        let result
        if (this.saveAction) {
          result = await this.saveAction(this.localNote)
        } else {
          this.$emit('save', this.localNote)
        }

        if (result !== false) {
          this.localNote = ''
          this.$emit('input', '')
        }
      } finally {
        this.clearPending('save')
      }
    },
    clearPending(eventName) {
      if (!this.pendingEvents[eventName]) return

      this.$delete(this.pendingEvents, eventName)
    },
  },
}
</script>

<style lang="scss" scoped>
.alert-center__comment-card {
  margin-top: 0.5rem;
  padding: 0.5rem;
  border: 1px solid $teal-100;
}

.alert-center__comment-title {
  font-size: 13px;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed $teal-100;
}

.mc-notes-input {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 38px;
  gap: 0.5rem;
  align-items: start;
  padding-top: 10px;
  width: 100%;

  :deep(.formulate-input) {
    min-width: 0;
    padding: 0;
    margin: 0;

    .formulate-input-wrapper,
    .formulate-input-element,
    input {
      width: 100%;
      height: 38px;
    }

    textarea {
      height: 38px;
    }
  }

  :deep(.mc-button-wrapper),
  :deep(button) {
    width: 38px;
    min-width: 38px;
    height: 38px;
  }

  :deep(button) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
}

.alert-center-comment__logs {
  display: flex;
  flex-direction: column;
  position: relative;
  margin-top: 1rem;
  max-height: 150px;
  overflow-y: auto;

  .user-logs {
    &__wrapper:first-child {
      .user-logs__item:nth-child(2) {
        .mc-fa {
          &:before {
            color: $cyan;
          }
        }
      }
    }

    &__wrapper {
      position: relative;
    }

    &__item {
      position: relative;
      display: flex;
      justify-content: space-between;
      gap: 8px;

      .mc-fa {
        flex: 0 0 auto;
        margin-right: 4px;
        vertical-align: middle;
        position: relative;
        z-index: 2;

        &:before {
          font-size: 18px;
          background-color: $white;
          color: $teal-200;
        }
      }

      &:last-child {
        .user-logs-content {
          &::before {
            display: none;
          }
        }
      }
    }
  }
}

.user-logs-content {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  position: relative;
  padding-bottom: 15px;

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: 8px;
    width: 0;
    height: 100%;
    border-left: 1px solid $teal-200;
    z-index: 1;
  }

  span {
    color: $teal-300;
    word-break: break-word;

    &.user-logs-user {
      color: $teal-400;
    }
  }
}

.user-logs-time {
  flex: 0 0 auto;
  white-space: nowrap;
  color: $teal-400;

  span {
    display: inline-block;
    width: 35px;
  }
}
</style>
